/**
 * Primary navigation model for the admin console (minimal counterpart of
 * web/src/config/navigation.tsx).
 *
 * The item list is derived, not static:
 *  - "Admin Actions" is admin-role only (support is read-only; the nav never
 *    shows them a write log).
 *  - "Account" appears ONLY while an account is open (the route param), so the
 *    sidebar/top bar link is derived from the URL — never from component state.
 *
 * NOTE: these gates are cosmetic. The server enforces the operator role on
 * every admin route regardless of what the navigation offers.
 */
import type { ComponentType } from 'react';
import { Search, ShieldCheck, User } from '@/components/icons';
import type { AdminRole } from '@/api/admin-types';

export interface AdminNavItem {
  /** Route label in the UI. */
  label: string;
  /** Internal application path. */
  to: string;
  /** Leading icon. */
  icon: ComponentType<{ className?: string }>;
  /** Optional short description for tooltips. */
  description?: string;
}

export interface AdminNavContext {
  /** The caller's operator role (from useAdminRole()). */
  role: AdminRole;
  /** The currently open account id, from the /accounts/:accountID route param. */
  openAccountID?: string;
}

export function getAdminNav(context?: AdminNavContext): AdminNavItem[] {
  const items: AdminNavItem[] = [
    {
      label: 'Lookup',
      to: '/',
      description: 'Find accounts, users, entitlements, and usage',
      icon: Search,
    },
  ];

  if (context?.openAccountID) {
    items.push({
      label: 'Account',
      to: `/accounts/${context.openAccountID}`,
      description: 'The currently open account',
      icon: User,
    });
  }

  if (!context || context.role === 'admin') {
    items.push({
      label: 'Admin Actions',
      to: '/actions',
      description: 'Manual plan grants, key revocation, account status, audit log',
      icon: ShieldCheck,
    });
  }

  return items;
}
